/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core';
import TrendingFlatOutlinedIcon from '@material-ui/icons/ArrowForwardRounded';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {VehicleEngineeringMode} from '@ion/core/src/types/entities';
import {StatusIndicator} from '@ion/dls/src/components/status/StatusIndicator';
import Render from '@ion/dls/src/render/Render';
import {DLSTheme, IconStatus} from '@ion/dls/src/types/types';
import {getVehicleEngineeringModeLabel} from '../VehicleEngineeringModeUtils';

export interface VehicleEngineeringModeCellProps {
  mode: VehicleEngineeringMode;
  pendingMode?: VehicleEngineeringMode;
  isConnected?: boolean;
  isScoutCapable?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  container : {
    display        : 'inline-flex',
    alignItems     : 'center',
    justifyContent : 'flex-start',
    height         : '100%',
    width          : '100%'
  },
  icon : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    margin         : '0px 2px 0px 8px',
    lineHeight     : 0
  }
}));

/**
 * VehicleEngineeringModeCell renders the vehicle's engineering mode alongside its
 * live MQTT connection status. The status indicator color reflects connectivity.
 * Green with a pulse when connected, grey when not.
 * When a pending mode change is in progress, an arrow and a pending indicator are
 * displayed to the right of the current mode.
 * When the device's Scout capability is known to be unsupported, an "Unsupported"
 * indicator is shown instead of the mode.
 *
 * ```typescript
 * import {VehicleEngineeringModeCell} from './VehicleEngineeringModeCell';
 *
 * <VehicleEngineeringModeCell mode={VehicleEngineeringMode.ONLINE} isConnected />
 * <VehicleEngineeringModeCell mode={VehicleEngineeringMode.ONLINE}
 *   pendingMode={VehicleEngineeringMode.OFFLINE} isConnected />
 * <VehicleEngineeringModeCell mode={VehicleEngineeringMode.ONLINE} isScoutCapable={false} />
 * ```
 *
 * @param mode           - The current engineering mode of the vehicle.
 * @param pendingMode    - Optional mode change that is currently in progress.
 * @param isConnected    - Whether the vehicle has an active MQTT connection. Defaults to false.
 * @param isScoutCapable - Whether the device supports Scout. Defaults to `true` so an unresolved
 *                         or inapplicable check never renders "Unsupported"; only an explicit
 *                         `false` does.
 */
export const VehicleEngineeringModeCell = ({
  mode,
  pendingMode,
  isConnected = false,
  isScoutCapable = true
}: VehicleEngineeringModeCellProps) => {
  const classes      = useStyles();
  const translations = translate('vehicle-manager:engineeringMode', {returnObjects: true}) || {};
  const currentLabel = getVehicleEngineeringModeLabel(translations, mode);
  const pendingLabel = getVehicleEngineeringModeLabel(translations, pendingMode);
  const hasPending   = !isNil(pendingMode) && pendingMode !== mode && !!pendingLabel;

  // Maps the MQTT connection state to an IconStatus color, green when connected, grey when not.
  const connectionStatusType = isConnected ? IconStatus.active : IconStatus.parked;

  return (
    <Render>
      <Render.If condition={!isScoutCapable}>
        <div className={classes.container}>
          <StatusIndicator text={translations.unsupported}
            type={IconStatus.disabled}
            variant='outlined' />
        </div>
      </Render.If>
      <Render.Else>
        <Render.If condition={!isNil(mode) && currentLabel}>
          <div className={classes.container}>
            <StatusIndicator text={currentLabel}
              type={connectionStatusType}
              pulse={isConnected} />
            <Render.If condition={hasPending}>
              <span className={classes.icon}>
                <TrendingFlatOutlinedIcon fontSize='small' color='disabled' />
              </span>
              <StatusIndicator text={pendingLabel}
                type={connectionStatusType}
                isPendingMode />
            </Render.If>
          </div>
        </Render.If>
      </Render.Else>
    </Render>
  );
};

export default memo(VehicleEngineeringModeCell);
